import { useState } from 'react'
import { Play, RefreshCw, RotateCw } from 'lucide-react'
import { useBackendHealth } from '../lib/query/freshness'

type Status = 'unknown' | 'up' | 'down' | 'starting' | 'restarting'
type Transition = { kind: 'starting' | 'restarting'; startedAt: number }

/** How long Start/Restart waits for /api/health before giving up. */
const TRANSITION_TIMEOUT_MS = 30_000

/**
 * Header-mounted dev control: shows the FastAPI backend's status and lets the
 * user start it when it's down. The "Start" button POSTs /__dev/start-backend,
 * which is handled by Vite's dev-only middleware (see vite.config.ts) — that
 * spawns scripts/start-backend.bat in a detached console window.
 *
 * Why this exists: when uvicorn is down, every /api/* call returns 502 from
 * Vite's proxy and the UI is unusable. The button must NOT route through the
 * backend (it'd 502 too), so it goes to Vite itself.
 *
 * Liveness comes from the shared `/api/health` query (lib/query/freshness.ts,
 * UI overhaul PR-2): 15 s for the whole app, 1 s while this control is
 * waiting for a start/restart to come back — the shared timer runs at the
 * fastest cadence any reader asks for.
 */
export default function BackendStatus({ allowRestart = import.meta.env.DEV }: { allowRestart?: boolean } = {}) {
  const [transition, setTransition] = useState<Transition | null>(null)
  const [error, setError] = useState<string | null>(null)

  // A transition is over once health has answered AFTER it began, or once it
  // has waited long enough; both are derived, so the fast poll stops on its
  // own and nothing has to set state from an effect.
  const health = useBackendHealth({ fast: transition !== null })
  const answeredSince = transition !== null && health.dataUpdatedAt > transition.startedAt
  const timedOut = transition !== null && !answeredSince
    && health.errorUpdatedAt - transition.startedAt >= TRANSITION_TIMEOUT_MS
  const inTransition = transition !== null && !answeredSince && !timedOut
  // The transition ends the moment health answers (or the wait runs out).
  // Clearing it here, during render (React's derive-state pattern), is what
  // returns the shared health probe to its 15 s cadence.
  if (transition !== null && (answeredSince || timedOut)) {
    setTransition(null)
    if (timedOut) setError('Backend did not respond within 30 seconds. Check the new "AdBetter Backend" console window.')
  }

  const status: Status = inTransition
    ? transition.kind
    : answeredSince ? 'up'
    : health.isError ? 'down'
    : health.data ? 'up'
    : 'unknown'

  const shownError = error

  async function startBackend() {
    setError(null)
    setTransition({ kind: 'starting', startedAt: Date.now() })
    try {
      const r = await fetch('/__dev/start-backend', { method: 'POST' })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    } catch (e) {
      setTransition(null)
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  // Restart: kills the live uvicorn on :8001 (via Vite-side .bat → taskkill)
  // and spawns a fresh one. Visible only when status === 'up' — there's
  // nothing to restart otherwise. The kill-then-spawn .bat runs detached
  // so we never expect a useful response body; we just flip into the
  // 'restarting' state and watch /api/health like Start does.
  async function restartBackend() {
    setError(null)
    setTransition({ kind: 'restarting', startedAt: Date.now() })
    try {
      const r = await fetch('/__dev/restart-backend', { method: 'POST' })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
    } catch (e) {
      // The fetch itself may fail if the proxied call somehow routed
      // through the backend (it shouldn't; the route is on Vite). Drop the
      // transition so the user gets the Start button as a recovery path.
      setTransition(null)
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const dot =
    status === 'up' ? 'bg-green-500'
    : status === 'starting' || status === 'restarting' ? 'bg-amber-400'
    : status === 'down' ? 'bg-red-500'
    : 'bg-gray-300'

  const label =
    status === 'up' ? 'Backend'
    : status === 'starting' ? 'Starting…'
    : status === 'restarting' ? 'Restarting…'
    : status === 'down' ? 'Backend down'
    : 'Backend'

  const tooltip = shownError
    ? `Backend issue — ${shownError}`
    : status === 'up' ? 'FastAPI backend is responding on :8001 — click Restart to reload code changes'
    : status === 'starting' ? 'Waiting for uvicorn to come up…'
    : status === 'restarting' ? 'Killed old uvicorn; waiting for the new one to bind :8001…'
    : status === 'down' ? 'FastAPI backend is not responding on :8001'
    : 'Checking backend status…'

  return (
    <div className="flex items-center gap-2 text-xs" title={tooltip}>
      <div className="flex items-center gap-1.5 px-2 py-1 border border-gray-200 rounded bg-white">
        <span className={`inline-block w-2 h-2 rounded-full ${dot}`} />
        <span className="text-gray-700">{label}</span>
      </div>
      {/* UI overhaul PR-3b (audit APP-11): these POST to Vite-only /__dev/*
          routes. In a production bundle they are compiled out — the real,
          admin-only restart lives on the System Health page behind a confirm. */}
      {status === 'up' && allowRestart && (
        <button
          onClick={restartBackend}
          title="Kill the running uvicorn and spawn a fresh one. Use this after backend code changes."
          className="flex items-center gap-1 px-2 py-1 border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50"
        >
          <RotateCw size={11} />
          <span>Restart</span>
        </button>
      )}
      {status === 'down' && allowRestart && (
        <button
          onClick={startBackend}
          className="flex items-center gap-1 px-2 py-1 border border-violet-300 bg-violet-50 text-violet-700 rounded hover:bg-violet-100"
        >
          <Play size={11} />
          <span>Start</span>
        </button>
      )}
      {(status === 'starting' || status === 'restarting') && (
        <span className="flex items-center gap-1 text-amber-600">
          <RefreshCw size={11} className="animate-spin" />
        </span>
      )}
    </div>
  )
}
